import { z } from 'zod';
import { CartModifierGroupSchema } from './CartModifierGroup';

const SelectedSizeSchema = z.object({
    sizeId: z.number().int().positive().optional(),
    sizeName: z.string().min(1).max(255) // For display only
});

/**
 * Composite ("choose N" / combo) structure — see ICartItem.isComposite.
 * `isComposite` marks the priced PARENT line; `bundleParentCartItemId` is set on
 * every CHILD and holds the parent's `cartItemId`. Declared, or
 * `CartSchema.parse` strips them from every synced cart: the parent stops being
 * a container, each pick reprices at its own menu price instead of the parent's
 * upcharge, and the kitchen loses the grouping. `CartItem.schemaParity.test.ts`
 * holds every own field of the model to this schema.
 */
const compositeLineFields = {
    isComposite: z.boolean().optional(),
    bundleParentCartItemId: z.string().min(1).optional() // same bound as cartItemId
};

export const CartItemSchema = z.object({
    cartItemId: z.string().min(1),
    menuItemId: z.number().int().positive(),
    menuItemGuid: z.string().optional(),
    menuGroupId: z.number().int().positive().optional(),
    // The ROOT→LEAF path the line was priced on (ICartItem.menuGroupPath).
    // Declared, or `CartSchema.parse` strips it from every synced cart.
    menuGroupPath: z.array(z.number().int().positive()).optional(),
    name: z.string().min(1).max(255),
    quantity: z.number().int().min(1),
    price: z.number().min(0), // Unit price (base or selected size price)
    selectedSize: SelectedSizeSchema.optional(),
    modifierGroups: z.array(CartModifierGroupSchema),
    notes: z.string().max(500).optional(),
    addedAt: z.date(),
    taxRateIds: z.array(z.number().int()),
    taxInclusion: z.boolean(),
    ...compositeLineFields
});

export const CartItemDataSchema = z.object({
    cartItemId: z.string().min(1).optional(),
    menuItemId: z.number().int().positive(),
    menuItemGuid: z.string().optional(),
    menuGroupId: z.number().int().positive().optional(),
    // The ROOT→LEAF path the line was priced on (ICartItem.menuGroupPath).
    // Declared, or `CartSchema.parse` strips it from every synced cart.
    menuGroupPath: z.array(z.number().int().positive()).optional(),
    name: z.string().min(1).max(255),
    quantity: z.number().int().min(1).default(1),
    price: z.number().min(0), // Unit price (base or selected size price)
    selectedSize: SelectedSizeSchema.optional(),
    modifierGroups: z.array(CartModifierGroupSchema).default([]),
    notes: z.string().max(500).optional(),
    addedAt: z.date().default(() => new Date()),
    taxRateIds: z.array(z.number().int()).default([]),
    taxInclusion: z.boolean().default(false),
    ...compositeLineFields
});

export type CartItemSchemaType = z.infer<typeof CartItemSchema>;
export type CartItemDataSchemaType = z.infer<typeof CartItemDataSchema>;
